import { ExternalLink, TriangleAlert } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { isApiError } from '../../api/client'
import { useAddSource, useCollections, useSourcePreview } from '../../api/queries'
import type { Platform, SourceAdded, SourcePreview } from '../../api/types'
import { openExternal } from '../../lib/download'
import { PLATFORM_LABEL, formatCheckTime, formatUsd, handleLabel } from '../../lib/format'
import { Avatar } from '../Avatar'
import { Button } from '../Button'
import { isTypingTarget } from '../../lib/cardControls'
import { useViewDraft } from '../../lib/useViewDraft'
import { Dialog } from '../Dialog'
import { Chip } from '../Select'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'

interface AddSourceDialogProps {
  /** Что подставить в поле: ник с репоста, убранный источник. */
  initialInput?: string
  /** Подборки, отмеченные сразу (с репоста — подборки канала, который репостнул). */
  initialCollectionIds?: number[]
  /** Площадка сразу: окно доски контент-плана открывает его со своей площадкой. */
  initialPlatform?: Platform
  /** Источник добавлен (или уже был): доска сразу выбирает его своим. */
  onAdded?: (res: SourceAdded) => void
  onClose: () => void
}

const BAD_INPUT = 'Не понял. Вставьте @ник, ссылку на профиль Instagram, t.me/канал или r/сабреддит.'

const INPUT_ERRORS: Record<string, string> = {
  bad_input: BAD_INPUT,
  bad_handle: 'Такого ника не бывает: проверьте буквы и длину.',
  post_link: 'Это ссылка на пост. Нужна ссылка на профиль: instagram.com/ник.',
  private_link: 'Закрытый канал по приглашению собрать нельзя: нужен публичный t.me/канал.',
}

function errorText(err: unknown, preview: SourcePreview | undefined): string {
  if (isApiError(err, 422)) return INPUT_ERRORS[err.detail ?? ''] ?? BAD_INPUT
  if (isApiError(err, 409) && err.detail === 'reddit_off') return 'Reddit выключен: нет ключей Reddit API.'
  if (isApiError(err, 409) && err.detail === 'limit_reached' && preview) return limitText(preview)
  if (isApiError(err, 400)) return 'Подборку только что удалили. Закройте окно и откройте снова.'
  return 'Не получилось. Попробуйте ещё раз.'
}

function limitText(p: SourcePreview): string {
  return `Потолок: активных ${PLATFORM_LABEL[p.platform]} ${p.active} из ${p.max_active ?? '∞'}. Поставьте кого-то на паузу или уберите.`
}

function addedText(res: SourceAdded, collections: number): string {
  const name = handleLabel(res.platform, res.handle)
  if (res.result === 'exists') return collections > 0 ? `${name} уже в источниках — положили в подборки` : `${name} уже в источниках`
  if (res.platform === 'instagram' && (!res.next_check_at || res.collection_mode === 'pending_activation')) return `${name} добавлен. Сбор Meta начнётся после активации`
  if (res.platform === 'instagram' && res.collection_mode === 'meta') return `${name} добавлен в сбор Meta`
  const when = formatCheckTime(res.next_check_at)
  const verb = res.result === 'reactivated' ? 'снова собирается' : 'добавлен'
  if (res.platform === 'telegram') return `${name} ${verb}. Проверим канал ${when}`
  return `${name} ${verb}. Первые посты — после обхода ${when}`
}

/** Поле ждёт паузы в наборе, прежде чем спрашивать сервер. */
function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

/** Новый источник: ник или ссылка → карточка с ценой → подборки → «Добавить».
 *  Тот же путь у кнопки на репосте и у «Вернуть» убранного источника. */
export function AddSourceDialog({
  initialInput = '',
  initialCollectionIds = [],
  initialPlatform,
  onAdded,
  onClose: finishClose,
}: AddSourceDialogProps) {
  const draftKey = `source.add.${initialPlatform ?? 'auto'}.${initialInput}`
  const [input, setInput, resetInput] = useViewDraft(`${draftKey}.input`, initialInput)
  const q = useDebounced(input.trim(), 350)
  const [platform, setPlatform, resetPlatform] = useViewDraft<Platform | null>(`${draftKey}.platform`, initialPlatform ?? null)
  const preview = useSourcePreview(q, platform)
  const collections = useCollections()
  const [picked, setPicked, resetPicked] = useViewDraft(`${draftKey}.picked`, initialCollectionIds)
  const onClose = () => { resetInput(); resetPlatform(); resetPicked(); finishClose() }
  const add = useAddSource()
  const submitted = useRef(false)

  // Под окном может быть карточка поста: она листается стрелками и ловит F и D.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.code === 'KeyF' || e.code === 'KeyD') e.preventDefault()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  const settled = input.trim() === q
  const p = settled && q !== '' ? preview.data : undefined
  const existing = p?.existing_state ?? null
  const disabled = !p || p.blocked !== null || add.isPending || (existing === 'active' && picked.length === 0)

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return [...next]
    })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!p || disabled || submitted.current) return
    submitted.current = true
    add.mutate(
      { input: q, platform: p.explicit ? undefined : p.platform, collection_ids: [...picked] },
      {
        onSettled: () => { submitted.current = false },
        onSuccess: (res) => {
          toast(addedText(res, picked.length), { ms: 5000 })
          onAdded?.(res)
          onClose()
        },
      },
    )
  }

  const label = existing === 'active' ? 'В подборки' : existing ? 'Вернуть' : 'Добавить'

  return (
    // Свайп в окне не должен листать карточку поста под ним.
    <div onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
      <Dialog
        title={existing === 'paused' || existing === 'removed' ? 'Вернуть источник' : 'Новый источник'}
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="primary" type="submit" form="add-source" disabled={disabled}>
              {add.isPending && <Spinner size={14} className="text-accent-fg" />}
              {label}
            </Button>
          </>
        }
      >
        <form id="add-source" onSubmit={submit}>
          <label className="mb-1 block text-xs text-muted" htmlFor="add-source-input">
            Ник или ссылка
          </label>
          <input
            id="add-source-input"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={300}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="@ник, instagram.com/ник, t.me/канал, r/сабреддит"
            className="h-11 w-full rounded-lg bg-surface px-3 text-[15px] ring-1 ring-line outline-none ring-inset focus:ring-fg/40 desk:h-9 desk:text-sm"
          />

          {q !== '' && (!settled || preview.isPending) && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}
          {settled && preview.isError && <p className="mt-3 text-sm text-danger">{errorText(preview.error, undefined)}</p>}
          {p && <PreviewCard preview={p} onPlatform={setPlatform} />}

          {p && p.blocked === null && (
            <Picker collections={collections.data ?? []} picked={new Set(picked)} onToggle={toggle} />
          )}

          {add.isError && <p className="mt-3 text-sm text-danger">{errorText(add.error, p)}</p>}
        </form>
      </Dialog>
    </div>
  )
}

function PreviewCard({ preview: p, onPlatform }: { preview: SourcePreview; onPlatform: (p: Platform) => void }) {
  const name = handleLabel(p.platform, p.handle)
  const existing = p.existing_state
  const when = formatCheckTime(p.next_check_at)
  const overCap = p.monthly_usd !== null && p.remaining_budget_usd != null && p.monthly_usd > p.remaining_budget_usd
  return (
    <div className="mt-3 flex flex-col gap-2.5 rounded-xl bg-surface p-3.5 ring-1 ring-line ring-inset">
      <div className="flex items-center gap-3">
        <Avatar name={p.handle} src={null} size={36} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate font-medium">{name}</div>
          <div className="mt-0.5 text-xs text-muted">
            {PLATFORM_LABEL[p.platform]}
            {existing === 'active' && ' · уже в источниках'}
            {existing === 'paused' && ' · на паузе, вернём в сбор'}
            {existing === 'removed' && ' · был убран, вернём в сбор'}
          </div>
        </div>
        {p.url && (
          <Button
            size="icon"
            variant="ghost"
            className="-mr-2"
            aria-label={`Открыть ${name} в ${PLATFORM_LABEL[p.platform]}`}
            title={`Открыть в ${PLATFORM_LABEL[p.platform]}`}
            onClick={() => openExternal(p.url ?? '')}
          >
            <ExternalLink size={15} />
          </Button>
        )}
      </div>

      {!p.explicit && p.platform !== 'reddit' && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted">Это</span>
          {(['instagram', 'telegram'] as const).map((value) => (
            <Chip key={value} active={p.platform === value} onClick={() => onPlatform(value)}>
              {PLATFORM_LABEL[value]}
            </Chip>
          ))}
        </div>
      )}

      {p.blocked === 'reddit_off' ? (
        <p className="text-sm text-danger">Reddit выключен: нет ключей Reddit API, сабреддиты не собираются.</p>
      ) : p.blocked === 'limit_reached' ? (
        <p className="text-sm text-danger">{limitText(p)}</p>
      ) : existing === 'active' ? (
        <p className="text-sm text-pretty">Уже собирается. Можно положить его в подборки.</p>
      ) : (
        <>
          <div>
            <div className="text-lg leading-tight font-semibold tabular-nums">
              {p.free ? 'Бесплатно' : p.cost_estimate?.min_monthly_usd != null && p.cost_estimate.max_monthly_usd != null
                ? `${formatUsd(p.cost_estimate.min_monthly_usd)}–${formatUsd(p.cost_estimate.max_monthly_usd)} за 30 дней после активации`
                : p.monthly_usd !== null ? `≈${formatUsd(p.monthly_usd)} за 30 дней после активации` : 'Для оценки нужна частота публикаций'}
            </div>
            <p className="mt-0.5 text-xs text-pretty text-muted">
              {p.free
                ? p.platform === 'telegram'
                  ? 'Telegram читается своей сессией, Apify не нужен.'
                  : 'Reddit читается через его API, Apify не нужен.'
                : 'Оценка включает недельную проверку окна 21 день и разовое обогащение новых публикаций. Зависит от активности автора и доли обогащаемых роликов.'}
            </p>
            {!p.free && <>
              {p.collection_mode === 'legacy_with_shadow' && <p className="mt-1.5 text-xs text-muted">Сейчас действует прежний сбор Apify. Сравнение Meta пока сохраняет его обычные расходы.</p>}
              <p className="mt-1.5 text-xs text-muted tabular-nums">Факт Apify за последние 30 дней: {formatUsd(p.spent_30d)}. Текущий месячный предел: {formatUsd(p.monthly_cap_usd)}.</p>
              {p.remaining_budget_usd != null && <p className="mt-1 text-xs text-muted tabular-nums">Доступно в месячном лимите: {formatUsd(p.remaining_budget_usd)}{p.reserved_budget_usd != null && ` · в резерве ${formatUsd(p.reserved_budget_usd)}`}</p>}
              {p.cost_estimate?.unpriced_components?.length ? <p className="mt-1 text-xs text-muted">Отдельно: музыка каждого ролика сразу, данные рекламы, расшифровка, перевод и сервер.</p> : null}
            </>}
            {overCap && (
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-pretty">
                <TriangleAlert size={14} className="mt-px shrink-0 text-danger" />Оценка выше доступного остатка. Платные задачи будут ждать бюджета.
              </p>
            )}
          </div>
          <p className="text-sm text-pretty">
            {p.platform === 'instagram' && (
              <>
                {p.collection_mode === 'pending_activation' || !p.next_check_at
                  ? 'Сбор Meta начнётся после активации источника.'
                  : p.collection_mode === 'meta' ? 'Первые публикации появятся после ближайшей проверки Meta.'
                    : `Ближайшая проверка текущего сбора — ${when}.`}
                {' '}Для ×N Meta нужен замер самого поста позже 48 часов после публикации и минимум 5 зрелых постов того же формата для сравнения.
              </>
            )}
            {p.platform === 'telegram' && (
              <>
                Канал проверим при ближайшем обходе, {when}. Если его нет или он закрыт, после трёх обходов подряд у
                источника будет статус «Ошибка».
              </>
            )}
            {p.platform === 'reddit' && <>Первые посты — после обхода {when}.</>}
          </p>
          {p.max_active !== null && (
            <p className="text-xs text-muted tabular-nums">
              Активных {PLATFORM_LABEL[p.platform]}: {p.active} из {p.max_active}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function Picker({
  collections,
  picked,
  onToggle,
}: {
  collections: { id: number; name: string }[]
  picked: Set<number>
  onToggle: (id: number) => void
}) {
  return (
    <div className="mt-3">
      <div className="mb-1.5 text-xs text-muted">Подборки</div>
      {collections.length === 0 ? (
        <p className="text-sm text-muted">Подборок пока нет — источник будет в «Без подборки».</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {collections.map((c) => (
            <Chip key={c.id} active={picked.has(c.id)} onClick={() => onToggle(c.id)}>
              {c.name}
            </Chip>
          ))}
        </div>
      )}
    </div>
  )
}
